import { Link, useSearchParams } from 'react-router-dom';
import { Bell, HelpCircle, Tv } from 'lucide-react';
import { toast } from 'sonner';
import type { MerchantMembership } from '@favelafood/shared/api/types';
import { StoreSwitcher } from '@favelafood/shared/components/merchant/shell/StoreSwitcher';
import { useOwnerMerchant, useToggleAccepting } from '../api/owner';

interface MerchantTopbarProps {
  variant: 'staff' | 'merchant';
  merchantId?: string | undefined;
  memberships?: MerchantMembership[] | undefined;
}

/** Toggle "Loja aberta" — only rendered when variant=merchant and merchantId is set. */
function AcceptingToggle({ merchantId }: { merchantId: string }): JSX.Element {
  const { data: merchant } = useOwnerMerchant(merchantId);
  const toggle = useToggleAccepting(merchantId);

  const accepting = merchant?.isAcceptingOrders ?? false;

  const handleClick = (): void => {
    toggle.mutate(!accepting, {
      onError: () => {
        toast.error('Não foi possível alterar o estado da loja. Tente novamente.');
      },
    });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={toggle.isPending}
      aria-pressed={accepting}
      aria-label={accepting ? 'Loja aberta — clique para fechar' : 'Loja fechada — clique para abrir'}
      className={[
        'flex items-center gap-1.5 rounded-[var(--m-r-pill)] px-3 py-1 text-xs font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-60',
        accepting
          ? 'bg-[var(--m-success-muted)] text-[var(--m-success)]'
          : 'bg-[var(--m-surface-muted)] text-[var(--m-fg-subtle)]',
      ].join(' ')}
    >
      <span
        className={[
          'inline-block h-1.5 w-1.5 rounded-full',
          accepting ? 'bg-[var(--m-success)]' : 'bg-[var(--m-fg-subtle)]',
        ].join(' ')}
        aria-hidden="true"
      />
      {accepting ? 'Loja aberta' : 'Loja fechada'}
    </button>
  );
}

export function MerchantTopbar({
  variant,
  merchantId,
  memberships,
}: MerchantTopbarProps): JSX.Element {
  const [, setParams] = useSearchParams();

  const handlePick = (id: string): void => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('merchantId', id);
      return next;
    });
  };

  const resolvedMemberships = memberships ?? [];
  const showSwitcher = variant === 'merchant';

  return (
    <header className="flex items-center justify-between border-b border-[var(--m-divider)] bg-[var(--m-surface)] px-6 py-3">
      <div className="flex items-center gap-2">
        {showSwitcher ? (
          <StoreSwitcher
            memberships={resolvedMemberships}
            currentId={merchantId ?? ''}
            onPick={handlePick}
          />
        ) : (
          <div className="text-sm font-medium text-[var(--m-fg)]">Onboarding</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {variant === 'merchant' && merchantId && (
          <Link
            to={`/kds?merchantId=${merchantId}`}
            className="m-cta flex items-center gap-2 px-3 py-1.5 text-xs"
          >
            <Tv size={14} /> Abrir KDS
          </Link>
        )}
        <div className="flex-1" />
        {variant === 'merchant' && merchantId && (
          <AcceptingToggle merchantId={merchantId} />
        )}
        <button
          aria-label="Notificações"
          className="rounded-[var(--m-r-md)] p-2 text-[var(--m-fg-muted)] hover:bg-[var(--m-surface-muted)]"
        >
          <Bell size={18} />
        </button>
        <button
          aria-label="Ajuda"
          className="rounded-[var(--m-r-md)] p-2 text-[var(--m-fg-muted)] hover:bg-[var(--m-surface-muted)]"
        >
          <HelpCircle size={18} />
        </button>
      </div>
    </header>
  );
}
